import React, { useEffect, useMemo, useState } from 'react';
import { notesApi } from '@/lib/notesApi';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Trash2, Loader2, X, Users, Check, ChevronsUpDown, ListChecks, CalendarDays, Bell } from 'lucide-react';
import { TIME_OPTIONS, todayStr, RECURRENCE_OPTIONS, recurrenceLabel, prettyDateLong, tzOffsetMin, timeToMin, addMinutesToTime, normalizeTime } from './plannerUtils';
import { GroupPeoplePicker } from './GroupPeoplePicker';

const field = 'mt-1 w-full';
const labelCls = 'text-[12px] font-semibold text-[color:var(--p-text-soft)]';

// Order linked tasks by due date — earliest first, undated last. Used in the
// Event/Meeting dialogs so the linked-task list reads top-down by urgency
// (mirrors the backend ordering).
const byDueDate = (a, b) => {
  const da = (a && a.due_at) || '';
  const db = (b && b.due_at) || '';
  if (!da && !db) return (a?.title || '').localeCompare(b?.title || '');
  if (!da) return 1;
  if (!db) return -1;
  if (da !== db) return da < db ? -1 : 1;
  return (a?.title || '').localeCompare(b?.title || '');
};

// Editable time field: users can TYPE a time (e.g. "4pm", "1630", "16:05") or
// pick from the 15-minute suggestion list. ``minTime`` (HH:MM) sets the floor
// the suggestion list starts from — used for the End field so it begins at the
// start time + 30 min. Typed values are normalised on blur/Enter; an empty
// field clears the time (null).
let _timeFieldSeq = 0;
const TimeField = ({ value, onChange, placeholder, testid, minTime }) => {
  const [text, setText] = React.useState(value || '');
  const listId = React.useMemo(() => { _timeFieldSeq += 1; return `tf-${testid || 'time'}-${_timeFieldSeq}`; }, [testid]);
  React.useEffect(() => { setText(value || ''); }, [value]);

  const options = React.useMemo(() => {
    const floor = minTime ? timeToMin(minTime) : -1;
    return TIME_OPTIONS.filter((t) => timeToMin(t) >= floor);
  }, [minTime]);

  const commit = (raw) => {
    const norm = normalizeTime(raw);
    if (norm === null) { setText(value || ''); return; }   // invalid → revert
    if (norm === '') { setText(''); onChange(null); return; } // cleared
    setText(norm); onChange(norm);
  };

  return (
    <>
      <Input
        list={listId}
        className={field}
        value={text}
        placeholder={placeholder}
        inputMode="numeric"
        autoComplete="off"
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(e.currentTarget.value); } }}
        data-testid={testid}
      />
      <datalist id={listId}>
        {options.map((t) => <option key={t} value={t} />)}
      </datalist>
    </>
  );
};

/* Reminder options. Must mirror the backend's allowed sets:
   timed  = [0,5,10,15,30,60,120,1440] minutes-before;
   allday = [0,1440,2880]  (morning-of / day-before / 2-days-before at 09:00). */
const REMINDER_TIMED = [
  { v: 0, label: 'At start time' },
  { v: 5, label: '5 minutes before' },
  { v: 10, label: '10 minutes before' },
  { v: 15, label: '15 minutes before' },
  { v: 30, label: '30 minutes before' },
  { v: 60, label: '1 hour before' },
  { v: 120, label: '2 hours before' },
  { v: 1440, label: '1 day before' },
];
const REMINDER_ALLDAY = [
  { v: 0, label: 'Morning of (9:00 AM)' },
  { v: 1440, label: 'A day before (9:00 AM)' },
  { v: 2880, label: '2 days before (9:00 AM)' },
];

/* Reminder picker — the option set adapts to all-day vs timed. ``value`` is the
   minutes-before offset (number) or null for "no reminder". */
const RemindSelect = ({ value, onChange, allDay, testid }) => {
  const opts = allDay ? REMINDER_ALLDAY : REMINDER_TIMED;
  return (
    <Select value={value === null || value === undefined ? 'none' : String(value)}
      onValueChange={(v) => onChange(v === 'none' ? null : Number(v))}>
      <SelectTrigger className={field} data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent className="max-h-64">
        <SelectItem value="none">No reminder</SelectItem>
        {opts.map((o) => <SelectItem key={o.v} value={String(o.v)}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

/* Recurrence picker shared by events + meetings. Shows a plain-English hint
   (e.g. "Monthly on the last Saturday") derived from the chosen date. */
const RecurrenceSelect = ({ value, onChange, date, testid }) => (
  <div>
    <Select value={value || 'none'} onValueChange={onChange}>
      <SelectTrigger className={field} data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>
        {RECURRENCE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
    {value && value !== 'none' && date && (
      <div className="mt-1 text-[11px] text-[color:var(--p-muted)]" data-testid="planner-recurrence-hint">{recurrenceLabel(value, date)}</div>
    )}
  </div>
);

const mergeTasksById = (a, b) => {
  const map = new Map();
  [...(a || []), ...(b || [])].forEach((t) => { if (t && t.id) map.set(t.id, { ...map.get(t.id), ...t }); });
  return Array.from(map.values());
};

/* Link existing tasks (or quick-create one) to an event/meeting. Shows live
   completion status so opening the event reveals how prep is going. */
const TaskLinkPicker = ({ value, onChange, directory, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const map = useMemo(() => Object.fromEntries((directory || []).map((t) => [t.id, t])), [directory]);
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  const quickCreate = async () => {
    const title = newTitle.trim();
    if (!title) return;
    setCreating(true);
    try {
      const { data } = await notesApi.post('/planner/tasks', { title });
      onCreated && onCreated(data);
      onChange([...value, data.id]);
      setNewTitle('');
    } catch (e) { toast.error('Could not create task'); } finally { setCreating(false); }
  };
  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid="planner-link-tasks-select"
            className="n-focus mt-1 flex w-full items-center justify-between gap-2 rounded-md border border-[var(--p-border)] bg-[var(--p-card)] px-3 py-2 text-left text-[13px] text-[color:var(--p-muted)] hover:border-[var(--p-accent)]">
            <span className="flex items-center gap-1.5"><ListChecks size={14} /> {value.length ? `${value.length} task${value.length > 1 ? 's' : ''} linked` : 'Link tasks'}</span>
            <ChevronsUpDown size={14} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="notes-scope w-[360px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search tasks…" />
            <CommandList className="max-h-56">
              <CommandEmpty>No tasks found.</CommandEmpty>
              <CommandGroup>
                {(directory || []).map((t) => (
                  <CommandItem key={t.id} value={`${t.title} ${t.id}`} onSelect={() => toggle(t.id)} data-testid={`planner-link-task-option-${t.id}`}>
                    <Check size={14} className={`mr-2 ${value.includes(t.id) ? 'opacity-100 text-[color:var(--p-accent)]' : 'opacity-0'}`} />
                    <span className={`min-w-0 truncate text-[13px] ${t.completed ? 'text-[color:var(--p-muted)] line-through' : 'text-[color:var(--p-text)]'}`}>{t.title}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          <div className="flex items-center gap-2 border-t border-[var(--p-line)] p-2">
            <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); quickCreate(); } }}
              placeholder="New task…" className="h-8 text-[13px]" data-testid="planner-link-task-create-input" />
            <Button type="button" size="sm" onClick={quickCreate} disabled={creating || !newTitle.trim()}
              className="h-8 bg-[var(--p-accent)] text-white hover:bg-[var(--p-accent-2)]" data-testid="planner-link-task-create-button">
              {creating ? <Loader2 size={14} className="animate-spin" /> : 'Add'}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      {value.length > 0 && (
        <div className="mt-2 space-y-1" data-testid="planner-linked-tasks-list">
          {[...value].sort((a, b) => byDueDate(map[a] || {}, map[b] || {})).map((id) => {
            const t = map[id];
            return (
              <div key={id} className="flex items-center gap-2 rounded-md border border-[var(--p-border)] bg-[var(--p-card-2)] px-2.5 py-1.5 text-[12.5px]" data-testid={`planner-linked-task-${id}`}>
                <span className={`grid h-4 w-4 shrink-0 place-items-center rounded-[5px] border ${t?.completed ? 'border-[var(--p-success)] bg-[var(--p-success)] text-white' : 'border-[var(--p-muted-2)]'}`}>{t?.completed && <Check size={11} strokeWidth={3} />}</span>
                <span className={`min-w-0 flex-1 truncate ${t?.completed ? 'text-[color:var(--p-muted)] line-through' : 'text-[color:var(--p-text)]'}`}>{t?.title || 'Task'}</span>
                <button type="button" onClick={() => toggle(id)} className="shrink-0 text-[color:var(--p-muted-2)] hover:text-[color:var(--p-danger)]" aria-label="Unlink task"><X size={12} /></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ---------------- Event ---------------- */
export const EventDialog = ({ open, onOpenChange, initial, defaultDate, onSaved, taskDirectory = [], directory = [], readOnly = false }) => {
  const editing = !!(initial && initial.id);
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(todayStr());
  const [allDay, setAllDay] = useState(false);
  const [endDate, setEndDate] = useState('');
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [desc, setDesc] = useState('');
  const [recur, setRecur] = useState('none');
  const [linkTasks, setLinkTasks] = useState([]);
  const [taskDir, setTaskDir] = useState([]);
  const [shared, setShared] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [reminder, setReminder] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(initial?.title || '');
    setDate(initial?.anchor_date || initial?.date || defaultDate || todayStr());
    setAllDay(!!initial?.all_day);
    setEndDate(initial?.end_date || '');
    setStart(initial?.start_time || null);
    setEnd(initial?.end_time || null);
    setDesc(initial?.description || '');
    setRecur(initial?.recurrence || 'none');
    setReminder(initial?.reminder_offset_min ?? null);
    setShared((initial?.shared_user_ids) || (initial?.shared_with || []).map((s) => s.id) || []);
    const its = initial?.tasks || [];
    setLinkTasks(its.map((t) => t.id));
    setTaskDir(mergeTasksById(taskDirectory, its));
  }, [open, initial, defaultDate, taskDirectory]);

  const dirMap = useMemo(() => Object.fromEntries((directory || []).map((u) => [u.id, u])), [directory]);
  const toggleShare = (id) => setShared((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  // Multi-day spans cannot repeat; a single-day all-day event (e.g. birthday) can.
  const isMultiDay = allDay && !!endDate && endDate > date;
  const toggleAllDay = (v) => {
    setAllDay(v);
    setReminder(null); // reminder option set differs between all-day and timed
    if (v) { setStart(null); setEnd(null); } else { setEndDate(''); }
  };
  // Start change auto-fills End to +30 min (editable); repairs End if now stale.
  const onStartChange = (v) => {
    setStart(v);
    if (v) setEnd((cur) => (!cur || timeToMin(cur) <= timeToMin(v)) ? addMinutesToTime(v, 30) : cur);
  };

  const save = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    if (allDay && endDate && endDate < date) { toast.error('End date must be on or after the start date'); return; }
    setBusy(true);
    try {
      const recurrence = isMultiDay ? 'none' : recur;
      const body = {
        title: title.trim(), date, description: desc, recurrence, task_ids: linkTasks,
        all_day: allDay,
        start_time: allDay ? null : start,
        end_time: allDay ? null : end,
        end_date: allDay && endDate ? endDate : null,
        shared_user_ids: shared,
        reminder_offset_min: reminder,
        tz_offset: tzOffsetMin(),
      };
      if (editing) await notesApi.put(`/planner/events/${initial.id}`, body);
      else await notesApi.post('/planner/events', body);
      toast.success(editing ? 'Event updated' : 'Event added', shared.length ? { description: `Shared with ${shared.length} ${shared.length > 1 ? 'people' : 'person'}` } : undefined);
      onOpenChange(false); onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save event'); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await notesApi.delete(`/planner/events/${initial.id}`); toast.success('Event deleted'); onOpenChange(false); onSaved && onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete'); } finally { setBusy(false); }
  };

  // ---- Read-only view (event shared with me; I'm not the owner) ----
  if (readOnly) {
    const whenText = initial?.all_day
      ? (initial?.end_date && initial.end_date > (initial.anchor_date || initial.date)
        ? `${prettyDateLong(initial.anchor_date || initial.date)} → ${prettyDateLong(initial.end_date)} · All day`
        : `${prettyDateLong(initial.anchor_date || initial.date)} · All day`)
      : `${prettyDateLong(initial?.anchor_date || initial?.date)}${initial?.start_time ? ` · ${initial.start_time}${initial?.end_time ? `–${initial.end_time}` : ''}` : ''}`;
    const sharedTasks = [...(initial?.tasks || [])].sort(byDueDate);
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="notes-scope sm:max-w-[440px] bg-[color:var(--p-card)] text-[color:var(--p-text)] border-[color:var(--p-border)]" data-testid="planner-event-readonly-modal">
          <DialogHeader><DialogTitle className="font-display text-xl">{initial?.title || 'Event'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {initial?.shared_by && (
              <div className="flex items-center gap-2 rounded-md border border-[color:var(--p-accent-soft)] bg-[color:var(--p-accent-bg)] px-3 py-2 text-[12.5px] text-[color:var(--p-text-soft)]" data-testid="planner-event-shared-by">
                <Users size={14} className="text-[color:var(--p-accent)]" /> Shared by <span className="font-semibold">{initial.shared_by}</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-[13px] text-[color:var(--p-text-soft)]">
              <CalendarDays size={15} className="text-[color:var(--p-accent)]" /> {whenText}
            </div>
            {initial?.recurring && (
              <div className="text-[12px] text-[color:var(--p-muted)]">Repeats {recurrenceLabel(initial.recurrence, initial.anchor_date || initial.date)}</div>
            )}
            {initial?.reminder_offset_min != null && (
              <div className="flex items-center gap-1.5 text-[12px] text-[color:var(--p-muted)]"><Bell size={12} className="text-[color:var(--p-accent)]" /> Reminder set</div>
            )}
            {initial?.description && (
              <div className="whitespace-pre-wrap rounded-md bg-[color:var(--p-card-2)] px-3 py-2 text-[13px] text-[color:var(--p-text)]">{initial.description}</div>
            )}
            {sharedTasks.length > 0 && (
              <div>
                <Label className={labelCls}>Linked tasks</Label>
                <div className="mt-1 space-y-1">
                  {sharedTasks.map((t) => (
                    <div key={t.id} className="flex items-center gap-2 rounded-md border border-[var(--p-border)] bg-[var(--p-card-2)] px-2.5 py-1.5 text-[12.5px]">
                      <span className={`grid h-4 w-4 shrink-0 place-items-center rounded-[5px] border ${t.completed ? 'border-[var(--p-success)] bg-[var(--p-success)] text-white' : 'border-[var(--p-muted-2)]'}`}>{t.completed && <Check size={11} strokeWidth={3} />}</span>
                      <span className={`min-w-0 flex-1 truncate ${t.completed ? 'text-[color:var(--p-muted)] line-through' : 'text-[color:var(--p-text)]'}`}>{t.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="mt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="planner-event-readonly-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="notes-scope sm:max-w-[460px] max-h-[90vh] overflow-y-auto bg-[color:var(--p-card)] text-[color:var(--p-text)] border-[color:var(--p-border)]" data-testid="planner-event-modal">
        <DialogHeader><DialogTitle className="font-display text-xl">{editing ? 'Edit event' : 'New event'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className={labelCls}>Title</Label>
            <Input className={field} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Design review" data-testid="planner-event-title-input" /></div>

          {/* All-day toggle */}
          <div className="flex items-center justify-between rounded-md border border-[color:var(--p-border)] bg-[color:var(--p-card-2)] px-3 py-2">
            <Label htmlFor="event-all-day" className="flex items-center gap-2 text-[12.5px] font-semibold text-[color:var(--p-text-soft)]">
              <CalendarDays size={15} className="text-[color:var(--p-accent)]" /> All-day event
            </Label>
            <Switch id="event-all-day" checked={allDay} onCheckedChange={toggleAllDay}
              data-testid="planner-event-all-day-switch"
              className="data-[state=checked]:bg-[var(--p-accent)] data-[state=unchecked]:bg-[color:var(--p-border)]" />
          </div>

          {allDay ? (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className={labelCls}>Start date</Label>
                <Input type="date" className={field} value={date} max={endDate || undefined} onChange={(e) => setDate(e.target.value)} data-testid="planner-event-date-input" /></div>
              <div><Label className={labelCls}>End date <span className="font-normal text-[color:var(--p-muted-2)]">(optional)</span></Label>
                <Input type="date" className={field} value={endDate} min={date} onChange={(e) => setEndDate(e.target.value)} data-testid="planner-event-end-date-input" /></div>
            </div>
          ) : (
            <div><Label className={labelCls}>Date</Label>
              <Input type="date" className={field} value={date} onChange={(e) => setDate(e.target.value)} data-testid="planner-event-date-input" /></div>
          )}

          {isMultiDay ? (
            <div className="rounded-md bg-[color:var(--p-accent-bg)]/50 px-3 py-2 text-[11.5px] text-[color:var(--p-muted)]" data-testid="planner-event-multiday-hint">
              Multi-day events span {Math.round((new Date(endDate) - new Date(date)) / 86400000) + 1} days and cannot repeat.
            </div>
          ) : (
            <div><Label className={labelCls}>Repeat</Label>
              <RecurrenceSelect value={recur} onChange={setRecur} date={date} testid="planner-event-recurrence-select" /></div>
          )}

          {!allDay && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className={labelCls}>Start</Label><TimeField value={start} onChange={onStartChange} placeholder="e.g. 16:00" testid="planner-event-start-select" /></div>
              <div><Label className={labelCls}>End</Label><TimeField value={end} onChange={setEnd} placeholder="e.g. 16:30" testid="planner-event-end-select" minTime={start ? addMinutesToTime(start, 30) : null} /></div>
            </div>
          )}

          {/* Reminder */}
          <div><Label className={labelCls}><span className="inline-flex items-center gap-1.5"><Bell size={13} className="text-[color:var(--p-accent)]" /> Reminder</span></Label>
            <RemindSelect value={reminder} onChange={setReminder} allDay={allDay} testid="planner-event-reminder-select" /></div>

          {/* Share with */}
          <div>
            <Label className={labelCls}>Share with <span className="font-normal text-[color:var(--p-muted-2)]">(optional)</span></Label>
            <GroupPeoplePicker
              value={shared}
              onChange={setShared}
              directory={directory}
              testidPrefix="planner-event-share"
              renderLabel={(n) => (n ? `Shared with ${n}` : 'Share with people or a group')}
              emptyLabel="Share with people or a group"
            />
          </div>

          <div><Label className={labelCls}>Description</Label>
            <Textarea className={field} rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Optional" data-testid="planner-event-description-textarea" /></div>
          <div><Label className={labelCls}>Linked tasks <span className="font-normal text-[color:var(--p-muted-2)]">(optional)</span></Label>
            <TaskLinkPicker value={linkTasks} onChange={setLinkTasks} directory={taskDir} onCreated={(t) => setTaskDir((d) => mergeTasksById(d, [t]))} /></div>
        </div>
        <DialogFooter className="mt-2 flex items-center justify-between sm:justify-between">
          {editing ? (
            <Button type="button" variant="ghost" onClick={remove} disabled={busy} className="text-[color:var(--p-danger)] hover:bg-[color:var(--p-accent-bg)]" data-testid="planner-event-delete-button"><Trash2 size={15} className="mr-1" /> Delete</Button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
            <Button type="button" onClick={save} disabled={busy} data-testid="planner-event-save-button" className="bg-[var(--p-accent)] text-white hover:bg-[var(--p-accent-2)]">{busy && <Loader2 size={15} className="mr-1 animate-spin" />} Save</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---------------- Meeting ---------------- */
export const MeetingDialog = ({ open, onOpenChange, initial, defaultDate, directory, onSaved, taskDirectory = [] }) => {
  const editing = !!(initial && initial.id);
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(todayStr());
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [desc, setDesc] = useState('');
  const [recur, setRecur] = useState('none');
  const [attendees, setAttendees] = useState([]);
  const [linkTasks, setLinkTasks] = useState([]);
  const [taskDir, setTaskDir] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [reminder, setReminder] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(initial?.title || '');
    setDate(initial?.anchor_date || initial?.date || defaultDate || todayStr());
    setStart(initial?.start_time || null);
    setEnd(initial?.end_time || null);
    setDesc(initial?.description || '');
    setAttendees((initial?.attendees || []).map((a) => a.id));
    setRecur(initial?.recurrence || 'none');
    setReminder(initial?.reminder_offset_min ?? null);
    const its = initial?.tasks || [];
    setLinkTasks(its.map((t) => t.id));
    setTaskDir(mergeTasksById(taskDirectory, its));
  }, [open, initial, defaultDate, taskDirectory]);

  const dirMap = useMemo(() => Object.fromEntries((directory || []).map((u) => [u.id, u])), [directory]);
  const toggleAtt = (id) => setAttendees((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  // Picking/typing a Start time auto-fills End to +30 min (editable), and
  // repairs End if it's now at/before the new Start.
  const onStartChange = (v) => {
    setStart(v);
    if (v) setEnd((cur) => (!cur || timeToMin(cur) <= timeToMin(v)) ? addMinutesToTime(v, 30) : cur);
  };

  const save = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    setBusy(true);
    try {
      const body = { title: title.trim(), date, start_time: start, end_time: end, description: desc, attendee_ids: attendees, recurrence: recur, task_ids: linkTasks, reminder_offset_min: reminder, tz_offset: tzOffsetMin() };
      if (editing) await notesApi.put(`/planner/meetings/${initial.id}`, body);
      else await notesApi.post('/planner/meetings', body);
      toast.success(attendees.length ? 'Meeting saved \u00b7 attendees notified' : 'Meeting saved', { id: 'planner-meeting-notification-toast' });
      onOpenChange(false); onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save meeting'); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await notesApi.delete(`/planner/meetings/${initial.id}`); toast.success('Meeting deleted'); onOpenChange(false); onSaved && onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete'); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="notes-scope sm:max-w-[480px] bg-[color:var(--p-card)] text-[color:var(--p-text)] border-[color:var(--p-border)]" data-testid="planner-meeting-modal">
        <DialogHeader><DialogTitle className="font-display text-xl">{editing ? 'Edit meeting' : 'New meeting'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className={labelCls}>Title</Label>
            <Input className={field} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Weekly sync" data-testid="planner-meeting-title-input" /></div>
          <div><Label className={labelCls}>Date</Label>
            <Input type="date" className={field} value={date} onChange={(e) => setDate(e.target.value)} data-testid="planner-meeting-date-input" /></div>
          <div><Label className={labelCls}>Repeat</Label>
            <RecurrenceSelect value={recur} onChange={setRecur} date={date} testid="planner-meeting-recurrence-select" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className={labelCls}>Start</Label><TimeField value={start} onChange={onStartChange} placeholder="e.g. 16:00" testid="planner-meeting-start-select" /></div>
            <div><Label className={labelCls}>End</Label><TimeField value={end} onChange={setEnd} placeholder="e.g. 16:30" testid="planner-meeting-end-select" minTime={start ? addMinutesToTime(start, 30) : null} /></div>
          </div>
          <div><Label className={labelCls}><span className="inline-flex items-center gap-1.5"><Bell size={13} className="text-[color:var(--p-accent)]" /> Reminder</span></Label>
            <RemindSelect value={reminder} onChange={setReminder} allDay={false} testid="planner-meeting-reminder-select" /></div>
          <div>
            <Label className={labelCls}>Attendees</Label>
            <GroupPeoplePicker
              value={attendees}
              onChange={setAttendees}
              directory={directory}
              testidPrefix="planner-meeting-attendee"
              renderLabel={(n) => (n ? `${n} invited` : 'Add attendees or a group')}
              emptyLabel="Add attendees or a group"
            />
          </div>
          <div><Label className={labelCls}>Description</Label>
            <Textarea className={field} rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Optional" data-testid="planner-meeting-description-textarea" /></div>
          <div><Label className={labelCls}>Linked tasks <span className="font-normal text-[color:var(--p-muted-2)]">(e.g. prep)</span></Label>
            <TaskLinkPicker value={linkTasks} onChange={setLinkTasks} directory={taskDir} onCreated={(t) => setTaskDir((d) => mergeTasksById(d, [t]))} /></div>
        </div>
        <DialogFooter className="mt-2 flex items-center justify-between sm:justify-between">
          {editing ? (
            <Button type="button" variant="ghost" onClick={remove} disabled={busy} className="text-[color:var(--p-danger)] hover:bg-[color:var(--p-accent-bg)]" data-testid="planner-meeting-delete-button"><Trash2 size={15} className="mr-1" /> Delete</Button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
            <Button type="button" onClick={save} disabled={busy} data-testid="planner-meeting-save-button" className="bg-[var(--p-accent)] text-white hover:bg-[var(--p-accent-2)]">{busy && <Loader2 size={15} className="mr-1 animate-spin" />} Save</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---------------- Goal ---------------- */
export const GoalDialog = ({ open, onOpenChange, initial, onSaved, directory = [] }) => {
  const editing = !!(initial && initial.id);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [type, setType] = useState('daily');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [target, setTarget] = useState('');
  const [unitLabel, setUnitLabel] = useState('');
  const [qty, setQty] = useState('');
  const [shared, setShared] = useState([]);
  const [countMode, setCountMode] = useState('per_member');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open) return;
    setTitle(initial?.title || '');
    setDesc(initial?.description || '');
    setType(initial?.type || 'daily');
    setStartDate(initial?.start_date || '');
    setEndDate(initial?.end_date || '');
    setTarget(initial?.target_count ? String(initial.target_count) : '');
    setUnitLabel(initial?.unit_label || '');
    setQty((initial?.quantity_target) ? String(initial.quantity_target) : '');
    setShared((initial?.shared_user_ids) || (initial?.shared_with || []).map((s) => s.id) || []);
    setCountMode(initial?.count_mode === 'team' ? 'team' : 'per_member');
    setErr('');
  }, [open, initial]);

  const isQuantity = !!unitLabel.trim();
  const unitWord = { daily: 'day', weekly: 'week', monthly: 'month' }[type] || 'period';

  const dirMap = useMemo(() => Object.fromEntries((directory || []).map((u) => [u.id, u])), [directory]);
  const toggleShare = (id) => setShared((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const save = async () => {
    setErr('');
    if (!title.trim()) { setErr('Title is required'); return; }
    if (!endDate) { setErr('End date is required'); return; }
    if (startDate && startDate > endDate) { setErr('Start date must be on or before end date'); return; }
    if (isQuantity) {
      const n = parseInt(qty, 10);
      if (!Number.isFinite(n) || n < 1) { setErr('Enter a target quantity of at least 1'); return; }
    }
    setBusy(true);
    try {
      const body = {
        title: title.trim(), description: desc, type,
        start_date: startDate || null, end_date: endDate,
        target_count: (!isQuantity && type === 'weekly' && target) ? Number(target) : null,
        unit_label: isQuantity ? unitLabel.trim() : null,
        quantity_target: isQuantity ? Number(qty) : null,
        shared_user_ids: shared,
        count_mode: countMode,
      };
      if (editing) await notesApi.put(`/planner/goals/${initial.id}`, body);
      else await notesApi.post('/planner/goals', body);
      toast.success(editing ? 'Goal updated' : 'Goal created');
      onOpenChange(false); onSaved && onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || 'Could not save goal'); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await notesApi.delete(`/planner/goals/${initial.id}`); toast.success('Goal deleted'); onOpenChange(false); onSaved && onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete'); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="notes-scope sm:max-w-[460px] bg-[color:var(--p-card)] text-[color:var(--p-text)] border-[color:var(--p-border)]" data-testid="planner-goal-modal">
        <DialogHeader><DialogTitle className="font-display text-xl">{editing ? 'Edit goal' : 'New goal'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className={labelCls}>Title</Label>
            <Input className={field} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Exercise daily" data-testid="planner-goal-title-input" /></div>
          <div><Label className={labelCls}>Type</Label>
            <Select value={type} onValueChange={setType} disabled={editing}>
              <SelectTrigger className={field} data-testid="planner-goal-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily goal</SelectItem>
                <SelectItem value="weekly">Weekly goal</SelectItem>
                <SelectItem value="monthly">Monthly goal</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="rounded-[var(--p-radius-md)] border border-[color:var(--p-border)] bg-[color:var(--p-card-2)] p-2.5">
            <Label className={labelCls}>Track a quantity <span className="font-normal text-[color:var(--p-muted-2)]">(optional)</span></Label>
            <div className="mt-1.5 grid grid-cols-[1fr_96px] gap-2">
              <Input
                className="m-0"
                value={unitLabel}
                onChange={(e) => setUnitLabel(e.target.value)}
                placeholder="Object e.g. creatives, new clients"
                maxLength={40}
                data-testid="planner-goal-unit-input"
              />
              <Input
                type="number" min={1} step={1}
                className="m-0"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                placeholder="Qty"
                disabled={!isQuantity}
                data-testid="planner-goal-qty-input"
              />
            </div>
            <div className="mt-1 text-[11px] text-[color:var(--p-muted)]" data-testid="planner-goal-qty-hint">
              {isQuantity
                ? `Target: ${qty || '?'} ${unitLabel.trim()} per ${unitWord}. Tick off each one as it\u2019s done.`
                : 'Leave the object blank to just track days done. Add an object to count how many per ' + unitWord + '.'}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className={labelCls}>Start date <span className="font-normal text-[color:var(--p-muted-2)]">(optional)</span></Label>
              <Input type="date" className={field} value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="planner-goal-start-date-input" /></div>
            <div><Label className={labelCls}>End date</Label>
              <Input type="date" className={field} value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="planner-goal-end-date-input" /></div>
          </div>
          {type === 'weekly' && !isQuantity && (
            <div>
              <Label className={labelCls}>Weekly target <span className="font-normal text-[color:var(--p-muted-2)]">(optional &middot; tick N days/week)</span></Label>
              <Select value={target || 'none'} onValueChange={(v) => setTarget(v === 'none' ? '' : v)}>
                <SelectTrigger className={field} data-testid="planner-goal-target-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No daily target (single weekly tick)</SelectItem>
                  {[1, 2, 3, 4, 5, 6, 7].map((n) => <SelectItem key={n} value={String(n)}>{n} day{n > 1 ? 's' : ''} / week</SelectItem>)}
                </SelectContent>
              </Select>
              {target && <div className="mt-1 text-[11px] text-[color:var(--p-muted)]" data-testid="planner-goal-target-hint">Shows every day &middot; the week auto-completes once ticked {target} time{Number(target) > 1 ? 's' : ''}.</div>}
            </div>
          )}
          <div>
            <Label className={labelCls}>Share with team <span className="font-normal text-[color:var(--p-muted-2)]">(optional &middot; each member can tick &amp; add a note)</span></Label>
            <GroupPeoplePicker
              value={shared}
              onChange={setShared}
              directory={directory}
              testidPrefix="planner-goal-share"
              renderLabel={(n) => (n ? `${n} teammate${n > 1 ? 's' : ''}` : 'Keep private')}
              emptyLabel="Keep private"
            />
          </div>
          {shared.length > 0 && (isQuantity || (type === 'weekly' && target)) && (
            <div data-testid="planner-goal-countmode">
              <Label className={labelCls}>{isQuantity ? 'Count the target as' : 'Count the weekly target as'}</Label>
              <div className="inline-flex rounded-md border border-[color:var(--p-border)] overflow-hidden text-[12px]">
                {[{ v: 'per_member', l: 'Per member' }, { v: 'team', l: 'Whole team' }].map((o) => (
                  <button key={o.v} type="button" onClick={() => setCountMode(o.v)}
                    aria-pressed={countMode === o.v}
                    className={`px-3 py-1.5 font-medium transition-colors ${countMode === o.v ? 'bg-[var(--p-accent)] text-white' : 'bg-transparent text-[color:var(--p-text-soft)] hover:bg-[var(--p-accent-bg)]'}`}
                    data-testid={`planner-goal-countmode-${o.v}`}>
                    {o.l}
                  </button>
                ))}
              </div>
              <div className="mt-1 text-[11px] text-[color:var(--p-muted)]">
                {isQuantity
                  ? (countMode === 'team'
                      ? `Everyone\u2019s logged ${unitLabel.trim() || 'items'} add up toward one shared target each ${unitWord}.`
                      : `Each member works toward ${qty || 'the'} ${unitLabel.trim() || 'items'} on their own each ${unitWord}.`)
                  : (countMode === 'team'
                      ? 'All members\u2019 ticks add up toward one shared weekly target.'
                      : 'Each member works toward the weekly target on their own.')}
              </div>
            </div>
          )}
          <div><Label className={labelCls}>Description</Label>
            <Textarea className={field} rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Optional" data-testid="planner-goal-description-textarea" /></div>
          {err && <div className="text-[12px] font-medium text-[color:var(--p-danger)]" data-testid="planner-goal-error">{err}</div>}
        </div>
        <DialogFooter className="mt-2 flex items-center justify-between sm:justify-between">
          {editing ? (
            <Button type="button" variant="ghost" onClick={remove} disabled={busy} className="text-[color:var(--p-danger)] hover:bg-[color:var(--p-accent-bg)]" data-testid="planner-goal-delete-button"><Trash2 size={15} className="mr-1" /> Delete</Button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
            <Button type="button" onClick={save} disabled={busy} data-testid="planner-goal-save-button" className="bg-[var(--p-accent)] text-white hover:bg-[var(--p-accent-2)]">{busy && <Loader2 size={15} className="mr-1 animate-spin" />} Save</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
